import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * <cart-gift-picker> — keeps the cart consistent with the free-gift picker
 * (snippets/cart-gift-picker.liquid). Rendered on every page by layout/theme.liquid
 * while at least one `gift_campaign` runs, in place of the legacy cart-gift-guard.js:
 *
 *   <cart-gift-picker data-campaigns='[{"code":"DAY11PRIME26","threshold":12500,
 *     "requiresEntry":true,"gifts":[4716…],"autoGift":0}, …]' hidden></cart-gift-picker>
 *
 * It renders nothing; it carries every running campaign and owns the listeners. It
 * sits in the layout, outside every section, so no section morph ever replaces it
 * and its listeners are registered exactly once per page. For the same reason it
 * is never re-rendered when the shopper enters another campaign's code, so it
 * picks the campaign from the cart on every scan (#active), in the order the
 * banner uses.
 *
 * The rule the shop wants: only the gift the shopper picks in the cart is free.
 * The picker marks that line with the property `_gift: true` and applies the
 * campaign code. The native Buy-X-get-Y discount behind the code cannot tell
 * lines apart: it frees ONE eligible unit per order and Shopify usually gives it
 * to the newest eligible line. So this component corrects the cart after each change:
 *
 * 0. Picked line but the code not on the cart: apply it through the cart API.
 * 1. Picked line with quantity > 1: one unit stays the gift, the rest becomes an
 *    ordinary purchase line, and the gift is re-added last so it keeps the free unit.
 * 2. Picked line charged while another line got the free unit (a gift set added
 *    from a product page): re-add the picked line once so it is the newest again.
 *    Still charged after that: rule 3.
 * 3. Picked line charged and nothing discounted (cart below the minimum, campaign
 *    over, variant not in the discount): remove it. Except when the code only lost
 *    to another code it does not combine with (Shopify keeps the code that saves
 *    more): then the gift wins, see #giftWins.
 * 4. No picked line left but the campaign code still on the cart: remove the code,
 *    so a gift set bought from a product page is never made free.
 *
 * 5. Picked line that is not a gift of the cart's campaign (the shopper picked a
 *    gift, then entered another campaign's code): remove it.
 *
 * Codes: only the cart's campaign code may sit on the cart, and only while its
 * gift is picked. The other campaigns' codes are taken off with it (rules 0 and 4).
 *
 * Two campaign options, per campaign (snippets/gift-campaign-code.liquid):
 * - autoGift: the campaign has ONE gift. Once the cart qualifies (and the
 *   code was entered, see below) that gift is added by itself, once per time the
 *   cart crosses the minimum. A shopper who removes it with the trash icon is not
 *   given it again in this tab; the banner's button still lets them add it.
 * - requiresEntry: the banner only shows after the shopper entered the code
 *   (cart attribute `_gift_code`, set by assets/cart-discount-field.js). A code
 *   that reached the cart another way (a /discount/<code> link) sets it here.
 *
 * Changes are made through the AJAX cart API, then a CartUpdateEvent lets the cart
 * page / drawer re-render (component-cart-items.js re-renders when the event has no
 * sections).
 */

const MARKER = '_gift';
const MOVED_KEY = 'cura:gift-moved';
const AUTO_KEY = 'cura:gift-auto-added';
const DECLINED_KEY = 'cura:gift-declined';
const ENTRY_ATTRIBUTE = '_gift_code';
// A shopper's code taken off so the gift could stay free (#giftWins); the discount
// field (assets/cart-discount-field.js) reads it once and shows it with the error.
const SET_ASIDE_KEY = 'cura:gift-set-aside';
const SOURCE = 'cart-gift-picker';

/**
 * @typedef {{ key: string, variant_id: number, quantity: number, properties?: Record<string, string>,
 *   final_line_price: number, original_line_price: number, line_level_total_discount: number }} CartItem
 * @typedef {{ items: CartItem[], discount_codes?: { code: string, applicable?: boolean }[], item_count: number,
 *   attributes?: Record<string, string> }} Cart
 * @typedef {{ code: string, threshold: number, requiresEntry: boolean, gifts: number[],
 *   autoGift: number }} Campaign
 */

/** @param {unknown} a @param {unknown} b */
const same = (a, b) => String(a).trim().toUpperCase() === String(b).trim().toUpperCase();

/** sessionStorage flag helpers; without storage the flag reads as set (never loop). */
const flag = {
  /** @param {string} key */
  get(key) {
    try {
      return sessionStorage.getItem(key) === '1';
    } catch {
      return true;
    }
  },
  /** @param {string} key @param {boolean} on */
  set(key, on) {
    try {
      if (on) sessionStorage.setItem(key, '1');
      else sessionStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};

/** @param {CartItem} item */
const isPicked = (item) => !!item.properties && item.properties[MARKER] === 'true';

/**
 * The JSON endpoint of a cart route. `Theme.routes.cart_add_url` already ends in
 * `.js` (snippets/scripts.liquid) while the others do not, so append it only where
 * missing: the old script appended it blindly and posted re-adds to /cart/add.js.js.
 * @param {'cart_url' | 'cart_add_url' | 'cart_change_url' | 'cart_update_url'} name
 */
const route = (name) => {
  const url = Theme.routes[name];
  return url.endsWith('.js') ? url : `${url}.js`;
};

// At most one re-add per situation per tab session: Shopify does not always hand
// the free unit to the newest line (seen with a quantity-2 line), so retrying
// would reshuffle the cart on every page load. A situation is the picked variant
// plus the lines holding the free unit: a NEW line taking it (a second gift set
// added from a product page) gets its own attempt.

/** @param {string} signature */
function alreadyMoved(signature) {
  try {
    return !!JSON.parse(sessionStorage.getItem(MOVED_KEY) || '{}')[signature];
  } catch {
    return true; // no storage: never re-add, so there can be no loop
  }
}

/** @param {string} signature */
function markMoved(signature) {
  try {
    const moved = JSON.parse(sessionStorage.getItem(MOVED_KEY) || '{}');
    moved[signature] = Date.now();
    sessionStorage.setItem(MOVED_KEY, JSON.stringify(moved));
  } catch {
    /* ignore */
  }
}

function forgetMoved() {
  try {
    sessionStorage.removeItem(MOVED_KEY);
  } catch {
    /* ignore */
  }
}

class CartGiftPicker extends Component {
  #busy = false;
  #pending = false; // a cart change arrived while busy: look again afterwards

  connectedCallback() {
    super.connectedCallback();

    // Capture phase on WINDOW for the remove button, see #onRemoveClick.
    window.addEventListener('click', this.#onRemoveClick, true);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.addEventListener('prerenderingchange', this.#rescan);
    window.addEventListener('pageshow', this.#onPageShow);

    this.#scan();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('click', this.#onRemoveClick, true);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener('prerenderingchange', this.#rescan);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /**
   * Every running campaign, read from the element's own attribute on every use.
   * (The old script read its code via document.currentScript, which came back
   * empty on a prerendered product page, so it silently did nothing there.)
   * @returns {Campaign[]}
   */
  get campaigns() {
    try {
      const list = JSON.parse(this.dataset.campaigns || '[]');
      return Array.isArray(list)
        ? list
            .filter((c) => c && String(c.code || '').trim())
            .map((c) => ({
              code: String(c.code).trim(),
              threshold: Number(c.threshold) || 0,
              requiresEntry: c.requiresEntry === true,
              gifts: Array.isArray(c.gifts) ? c.gifts.map(Number) : [],
              autoGift: Number(c.autoGift) || 0,
            }))
        : [];
    } catch {
      return [];
    }
  }

  /**
   * The campaign this cart is shown. Same order as snippets/cart-gift-picker.liquid
   * (keep in sync): the code the shopper entered (`_gift_code`); a campaign code
   * already on the cart (Liquid cannot see those; rule "record entry" below writes
   * it to `_gift_code` so the next render agrees); a campaign whose gift was
   * picked; the first campaign shown without a code; the first campaign.
   * @param {Cart} cart
   * @returns {Campaign | null}
   */
  #active(cart) {
    const campaigns = this.campaigns;
    const entered = String(cart.attributes?.[ENTRY_ATTRIBUTE] || '').trim();
    const codes = (cart.discount_codes || []).map((d) => String(d.code));
    const pickedIds = cart.items.filter(isPicked).map((item) => item.variant_id);
    return (
      (entered && campaigns.find((c) => same(c.code, entered))) ||
      campaigns.find((c) => codes.some((code) => same(code, c.code))) ||
      campaigns.find((c) => c.gifts.some((id) => pickedIds.includes(id))) ||
      campaigns.find((c) => !c.requiresEntry) ||
      campaigns[0] ||
      null
    );
  }

  /**
   * Every code on the cart that is not a campaign code: the campaign codes are
   * this component's, every other code is the shopper's and is always kept.
   * @param {Cart} cart
   */
  #otherCodes(cart) {
    const managed = this.campaigns.map((c) => c.code);
    return (cart.discount_codes || [])
      .map((d) => String(d.code))
      .filter((code) => !managed.some((m) => same(m, code)));
  }

  // ---- Event handlers -----------------------------------------------------------

  /** @param {Event} event */
  #onCartUpdate = (event) => {
    // Not an instanceof check: CartAddEvent and plain CustomEvents share the name.
    if (/** @type {CustomEvent} */ (event).detail?.data?.source === SOURCE) return; // our own dispatch
    this.#scan();
  };

  // A prerendered page ran the scan before the shopper got there (the cart may have
  // changed since), and a page restored from the back/forward cache runs no
  // connectedCallback at all: look again when either is actually shown.
  #rescan = () => {
    this.#scan();
  };

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (event.persisted) this.#scan();
  };

  /**
   * Removing the picked gift with the trash icon. The theme's own handler removes
   * only the line; the code stays on the cart until rule 4 runs, and in between the
   * discount hands the free unit to another gift set in the cart (the "Free gift"
   * badge jumped to a product the shopper is paying for, for ~3 s). So the click is
   * taken over here and the line and the code go in ONE /cart/update.
   * Capture phase on WINDOW: the theme's declarative `on:click` handler is itself a
   * capture listener on document, registered earlier, so a document listener runs
   * too late to stop it (verified: both requests went out and raced).
   * @param {MouseEvent} event
   */
  #onRemoveClick = async (event) => {
    const button = event.target instanceof Element && event.target.closest('.cart-items__remove');
    const row = button && button.closest('[data-gift-line][data-key]');
    if (!row || !this.campaigns.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    // Not gated on #busy: the click is already taken from the theme, so it must
    // always act. A scan running in parallel is re-run afterwards.
    this.#busy = true;
    row.classList.add('removing'); // the theme's own row-removal animation

    try {
      const cart = await this.#getCart();
      // The shopper does not want the gift: never add this campaign's single gift
      // back by itself.
      const campaign = this.#active(cart);
      if (campaign) flag.set(`${DECLINED_KEY}:${campaign.code.toUpperCase()}`, true);
      const key = row.getAttribute('data-key') ?? '';
      await this.#post('cart_update_url', { updates: { [key]: 0 }, discount: this.#otherCodes(cart).join(',') });
      await this.#announce();
    } catch {
      row.classList.remove('removing');
    } finally {
      this.#busy = false;
      this.#scan(); // whatever happened above, let the rules settle the cart
    }
  };

  // ---- Cart API -----------------------------------------------------------------

  /** @returns {Promise<Cart>} */
  async #getCart() {
    const response = await fetch(route('cart_url'), { credentials: 'same-origin' });
    return response.json();
  }

  /**
   * @param {'cart_add_url' | 'cart_change_url' | 'cart_update_url'} routeName
   * @param {object} body
   */
  async #post(routeName, body) {
    const response = await fetch(route(routeName), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    return response.json();
  }

  /**
   * @param {CartItem} item
   * @param {number} quantity
   */
  #readd(item, quantity) {
    return this.#post('cart_add_url', {
      items: [{ id: item.variant_id, quantity, properties: item.properties }],
    });
  }

  async #announce() {
    const cart = await this.#getCart();
    document.dispatchEvent(new CartUpdateEvent(cart, SOURCE, { source: SOURCE, itemCount: cart?.item_count }));
  }

  // ---- Rules --------------------------------------------------------------------

  /**
   * Returns ONE fix (a function returning a promise) or null when the cart is fine.
   * @param {Cart} cart
   * @returns {(() => Promise<unknown>) | null}
   */
  #decide(cart) {
    const campaign = this.#active(cart);
    if (!campaign) return null;
    const CODE = campaign.code;
    const autoKey = `${AUTO_KEY}:${CODE.toUpperCase()}`;
    const declinedKey = `${DECLINED_KEY}:${CODE.toUpperCase()}`;
    const picked = cart.items.filter(isPicked);

    // The single-gift memories belong to the campaign the cart is shown. A shopper
    // who switches to another campaign's code and back has entered this one again,
    // so its single gift is added again (once) rather than staying "already added".
    for (const other of this.campaigns) {
      if (same(other.code, CODE)) continue;
      flag.set(`${AUTO_KEY}:${other.code.toUpperCase()}`, false);
      flag.set(`${DECLINED_KEY}:${other.code.toUpperCase()}`, false);
    }

    // Rule 5: a picked gift of ANOTHER campaign (picked before the shopper entered
    // this campaign's code). It is not this campaign's gift, so it goes; this
    // campaign's own gift is then picked or added as usual.
    const stale = picked.filter((item) => !campaign.gifts.includes(item.variant_id));
    if (stale.length) {
      /** @type {Record<string, number>} */
      const updates = {};
      for (const item of stale) updates[item.key] = 0;
      return () => this.#post('cart_update_url', { updates });
    }

    // The one-re-add memory (rule 2) belongs to the gift currently in the cart.
    // Once no gift is picked it is reset, so a later pick gets its own attempt.
    if (!picked.length) forgetMoved();

    const anyDiscounted = cart.items.some((item) => item.line_level_total_discount > 0);
    const codes = (cart.discount_codes || []).map((d) => String(d.code));
    const others = this.#otherCodes(cart);

    // Rule 0: a picked gift and the code not on the cart yet (the picker's form
    // only adds the line), or another campaign's code still there: the codes
    // become the shopper's own plus this campaign's, through the cart API. Not via
    // `/discount/<code>`, which keeps the code attached to the cart for checkout
    // even after rule 4 removes it, so a set bought from a product page could
    // still go out free (verified on curaprox-ae 2026-10-07).
    const hasCode = codes.some((c) => same(c, CODE));
    const foreignCampaignCode = codes.length - others.length > (hasCode ? 1 : 0);
    if (picked.length && (!hasCode || foreignCampaignCode)) {
      return () => this.#post('cart_update_url', { discount: [...others, CODE].join(',') });
    }

    // Rule 1. Count units, not lines: when the shopper raises the gift's quantity,
    // Shopify splits it into two `_gift` lines (one free, one charged), so a
    // quantity check alone never fires.
    const pickedUnits = picked.reduce((sum, item) => sum + item.quantity, 0);
    const keep = picked[0]; // cart.items lists the newest line first: the latest pick
    if (pickedUnits > 1 && keep) {
      /** @type {Record<string, number>} */
      const updates = {};
      let extraUnits = -1; // further units of the kept variant: an ordinary purchase
      for (const item of picked) {
        updates[item.key] = 0;
        if (item.variant_id === keep.variant_id) extraUnits += item.quantity;
      }
      // Picked lines of OTHER variants are earlier picks (a second tab, a page
      // resubmitted from history): the shopper chose a gift there, not a
      // purchase, so those lines are dropped rather than turned into paid ones.
      return async () => {
        await this.#post('cart_update_url', { updates });
        if (extraUnits > 0) {
          await this.#post('cart_add_url', { items: [{ id: keep.variant_id, quantity: extraUnits }] });
        }
        // Added last so it is the newest line and takes the free unit.
        return this.#readd(keep, 1);
      };
    }

    for (const item of picked) {
      const charged = item.final_line_price > 0 && item.line_level_total_discount === 0;
      if (!charged) continue;

      // Rule 2
      if (anyDiscounted) {
        const freeLines = cart.items
          .filter((other) => other.line_level_total_discount > 0)
          .map((other) => `${other.key}x${other.quantity}`)
          .join(',');
        const signature = `${item.variant_id}>${freeLines}`;
        // Re-added once and still charged (the variant is not in the discount,
        // or Shopify kept the free unit elsewhere): fall through to rule 3. A
        // charged gift is no gift, and once it is gone rule 4 takes the code
        // off, so the set the shopper bought is never the one made free.
        if (!alreadyMoved(signature)) {
          return async () => {
            markMoved(signature);
            await this.#post('cart_change_url', { id: item.key, quantity: 0 });
            return this.#readd(item, item.quantity);
          };
        }
      }

      // Rule 3, unless the code is on the cart and Shopify set it aside for one of
      // the shopper's codes: the gift wins (or, failing that, goes after all).
      const own = (cart.discount_codes || []).find((d) => same(d.code, CODE));
      if (own && own.applicable === false && others.length) {
        return () => this.#giftWins(CODE, others, item);
      }
      return () => this.#post('cart_change_url', { id: item.key, quantity: 0 });
    }

    // The code reached the cart without the field (a /discount/<code> link): record
    // it as entered, so a campaign shown only after entry shows its banner, and so
    // the banner (which cannot see the cart's codes) shows this same campaign.
    const enteredCode = String(cart.attributes?.[ENTRY_ATTRIBUTE] || '').trim();
    const entered = hasCode || same(enteredCode, CODE);
    if (hasCode && !same(enteredCode, CODE) && (campaign.requiresEntry || this.campaigns.length > 1)) {
      return () => this.#post('cart_update_url', { attributes: { [ENTRY_ATTRIBUTE]: CODE } });
    }

    // Single-gift campaign: add the gift once the cart qualifies. Once per crossing
    // of the minimum (a gift the discount does not make free is removed by rule 3
    // and must not come straight back), never after the shopper removed it. Both
    // memories are per campaign code.
    if (picked.length) flag.set(declinedKey, false);
    // A code-only campaign whose code is not entered (never, or the shopper removed
    // it): forget both memories, so entering the code (again) adds the gift again.
    if (campaign.requiresEntry && !entered) {
      flag.set(autoKey, false);
      flag.set(declinedKey, false);
    }
    const qualifying = cart.items
      .filter((item) => !isPicked(item))
      .reduce((sum, item) => sum + (item.original_line_price || 0), 0);
    const qualifies = campaign.threshold > 0 && qualifying >= campaign.threshold;
    if (!qualifies) flag.set(autoKey, false);
    if (
      campaign.autoGift &&
      !picked.length &&
      qualifies &&
      (!campaign.requiresEntry || entered) &&
      !flag.get(declinedKey) &&
      !flag.get(autoKey)
    ) {
      flag.set(autoKey, true);
      return () =>
        this.#post('cart_add_url', {
          items: [{ id: campaign.autoGift, quantity: 1, properties: { [MARKER]: 'true' } }],
        });
    }

    // Rule 4: no gift picked, so no campaign code belongs on the cart.
    if (!picked.length && others.length !== codes.length) {
      return () => this.#post('cart_update_url', { discount: others.join(',') });
    }

    return null;
  }

  /**
   * The picked gift is charged because Shopify set the campaign code aside for a
   * shopper's code it does not combine with: of two such codes it keeps the one
   * that saves more (15% off a large order beats one free set), so the gift used to
   * vanish without a word. The shopper's last act was picking the gift, and one
   * order gets one gift, so the gift wins: the code alone first, then each other
   * code back in turn, kept only while both still apply (a combinable code such as
   * free shipping stays). The first code that had to go is handed to the discount
   * field, which shows it with its error. If the code does not apply even alone,
   * the shopper's codes go back and the gift is removed (rule 3).
   * @param {string} code
   * @param {string[]} others
   * @param {CartItem} item
   */
  async #giftWins(code, others, item) {
    /** @param {Cart} cart @param {string} c */
    const applies = (cart, c) => (cart.discount_codes || []).some((d) => same(d.code, c) && d.applicable !== false);

    let cart = /** @type {Cart} */ (await this.#post('cart_update_url', { discount: code }));
    if (!applies(cart, code)) {
      await this.#post('cart_update_url', { discount: [...others, code].join(',') });
      return this.#post('cart_change_url', { id: item.key, quantity: 0 });
    }

    /** @type {string[]} */
    const kept = [];
    /** @type {string[]} */
    const setAside = [];
    for (const other of others) {
      cart = await this.#post('cart_update_url', { discount: [...kept, other, code].join(',') });
      if (applies(cart, code) && applies(cart, other)) kept.push(other);
      else setAside.push(other);
    }
    // The last try may have been a code that had to go: settle on the kept ones.
    if (setAside.length) await this.#post('cart_update_url', { discount: [...kept, code].join(',') });

    if (setAside[0]) {
      try {
        sessionStorage.setItem(SET_ASIDE_KEY, setAside[0]);
      } catch {
        /* the code is still taken off; only the hint in the field is lost */
      }
    }
  }

  /**
   * A fix triggers a fresh look (up to 3 in a row) so dependent fixes chain, e.g.
   * rule 3 removes the gift, then rule 4 removes the code.
   * @param {number} [depth]
   */
  async #scan(depth = 0) {
    if (this.#busy) {
      this.#pending = true;
      return;
    }
    this.#busy = true;
    let changed = false;

    try {
      const fix = this.#decide(await this.#getCart());
      if (fix) {
        changed = true;
        await fix();
        await this.#announce();
      }
    } catch {
      /* network hiccup: try again on the next cart update */
    } finally {
      this.#busy = false;
      if (this.#pending) {
        // Someone else changed the cart meanwhile: start a fresh round.
        this.#pending = false;
        this.#scan(0);
      } else if (changed && depth < 3) {
        this.#scan(depth + 1);
      }
    }
  }
}

if (!customElements.get('cart-gift-picker')) {
  customElements.define('cart-gift-picker', CartGiftPicker);
}
